import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtINRCompact } from "@/lib/format";
import { ArrowRight, CalendarClock, RefreshCcw } from "lucide-react";

/**
 * VAULT Forecast — deterministic upcoming-obligation view.
 * Reads /api/forecast?horizon={7d|30d|90d|6m|1y}. Never writes.
 * Presented as organisational foresight — never as advice.
 */
const HORIZONS = [
    { key: "7d",  label: "7 Days"  },
    { key: "30d", label: "30 Days" },
    { key: "90d", label: "90 Days" },
    { key: "6m",  label: "6 Months"},
    { key: "1y",  label: "1 Year"  },
];

const SOURCE_TONE = {
    subscription: { color: "#F97316", label: "Subscription" },
    bill:         { color: "#3B82F6", label: "Bill" },
    renewal:      { color: "#F59E0B", label: "Renewal" },
    payment:      { color: "#8B5CF6", label: "Payment" },
};

export default function Forecast() {
    const [horizon, setHorizon] = useState("30d");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    async function load(h = horizon) {
        setLoading(true); setErr(null);
        try {
            const r = await api.get(`/forecast?horizon=${h}`);
            setData(r.data);
        } catch (e) { setErr(e?.response?.data?.detail || "Could not load forecast."); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(horizon); }, [horizon]);

    const groups = useMemo(() => {
        const g = {};
        (data?.obligations || []).forEach((o) => {
            (g[o.date] = g[o.date] || []).push(o);
        });
        return g;
    }, [data]);

    return (
        <div className="max-w-[1100px] mx-auto p-6" data-testid="forecast-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-stone-500 font-semibold flex items-center gap-2">
                        <CalendarClock size={13} strokeWidth={2.2}/> FUTURE · VAULT FORECAST
                    </div>
                    <h1 className="text-3xl font-serif font-medium text-stone-900 tracking-tight mt-2">
                        What&rsquo;s coming.
                    </h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-xl">
                        A deterministic view of upcoming obligations from your bills, subscriptions, renewals
                        and scheduled payments. Organisational foresight — never advice.
                    </p>
                </div>
                <button
                    onClick={() => load()}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-stone-300 text-sm hover:bg-stone-50"
                    data-testid="forecast-refresh"
                >
                    <RefreshCcw size={13}/> Refresh
                </button>
            </div>

            {/* Horizon chips */}
            <div className="inline-flex gap-1 p-1 bg-stone-100 rounded-full mb-6" role="tablist" data-testid="forecast-horizons">
                {HORIZONS.map((h) => (
                    <button
                        key={h.key}
                        role="tab"
                        aria-selected={horizon === h.key}
                        onClick={() => setHorizon(h.key)}
                        data-testid={`forecast-h-${h.key}`}
                        className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                            horizon === h.key
                                ? "bg-stone-900 text-stone-50"
                                : "text-stone-600 hover:text-stone-900"
                        }`}
                    >
                        {h.label}
                    </button>
                ))}
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8" data-testid="forecast-summary">
                <SummaryTile
                    eyebrow="Total scheduled"
                    value={data ? fmtINRCompact(data.total_scheduled) : "—"}
                    tone="ink"
                    loading={loading}
                />
                <SummaryTile
                    eyebrow="Obligations"
                    value={data ? String(data.count) : "—"}
                    tone="ink"
                    loading={loading}
                />
                <SummaryTile
                    eyebrow="Window"
                    value={data ? `${data.start.slice(5)} → ${data.end.slice(5)}` : "—"}
                    tone="muted"
                    loading={loading}
                />
            </div>

            {/* Group by source */}
            {data && Object.keys(data.by_source || {}).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6" data-testid="forecast-by-source">
                    {Object.entries(data.by_source).map(([src, amt]) => {
                        const t = SOURCE_TONE[src] || { color: "#78716C", label: src };
                        return (
                            <div key={src} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-50 border border-stone-200 text-sm">
                                <span className="w-2 h-2 rounded-full" style={{ background: t.color }}/>
                                <span className="text-stone-600">{t.label}</span>
                                <span className="text-stone-900 font-medium tabular-nums">{fmtINRCompact(amt)}</span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Obligations grouped by date */}
            {err && (
                <div className="p-6 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-sm" data-testid="forecast-error">
                    {err}
                </div>
            )}
            {!err && !loading && data && data.obligations.length === 0 && (
                <div className="p-8 rounded-2xl bg-emerald-50/50 border border-emerald-200 text-center" data-testid="forecast-empty">
                    <div className="text-emerald-900 font-serif text-2xl">Nothing scheduled.</div>
                    <div className="text-emerald-800/80 text-sm mt-1">
                        No known obligations in the next {HORIZONS.find(h => h.key === horizon)?.label.toLowerCase()}. You&rsquo;re all caught up.
                    </div>
                </div>
            )}
            {!err && data && data.obligations.length > 0 && (
                <div className="space-y-3" data-testid="forecast-list">
                    {Object.entries(groups).map(([d, rows]) => (
                        <div key={d} className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`forecast-day-${d}`}>
                            <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-stone-500">
                                    {new Date(d).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                                </div>
                                <div className="text-sm tabular-nums text-stone-700">
                                    {fmtINRCompact(rows.reduce((s, r) => s + (r.amount || 0), 0))}
                                </div>
                            </div>
                            <div className="divide-y divide-stone-100">
                                {rows.map((r) => {
                                    const t = SOURCE_TONE[r.source] || { color: "#78716C", label: r.source };
                                    return (
                                        <div key={`${r.source}-${r.id}`} className="px-5 py-3 flex items-center gap-3">
                                            <span className="w-1.5 h-8 rounded-full" style={{ background: t.color }}/>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-stone-900 truncate">{r.label}</div>
                                                <div className="text-xs text-stone-500">{t.label} · {r.category}</div>
                                            </div>
                                            <div className="text-sm tabular-nums text-stone-900 font-medium">
                                                {fmtINRCompact(r.amount)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <p className="text-xs text-stone-500 mt-8">
                {data?.note || "Deterministic upcoming-obligation view."}
                {" "}<Link to="/calendar" className="text-stone-700 underline decoration-stone-300">Open financial calendar <ArrowRight size={11} className="inline -mt-0.5"/></Link>
            </p>
        </div>
    );
}

function SummaryTile({ eyebrow, value, tone, loading }) {
    return (
        <div className={`p-5 bg-white border rounded-2xl ${tone === "muted" ? "border-stone-200" : "border-stone-300"}`}>
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-stone-500">{eyebrow}</div>
            <div className={`mt-2 font-serif text-3xl tabular-nums ${tone === "muted" ? "text-stone-600" : "text-stone-900"}`}>
                {loading ? "—" : value}
            </div>
        </div>
    );
}
